'use client';

import { useEffect, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { IconArrowSquareDown } from '@/components/svg-symbols/icons';
import { cn } from '@/utils/cn';
import { InlineMessage } from './inline-message';
import { DropdownItem, InlineMessageType } from './types';
interface Props<TItem> {
  title?: string;
  defaultState?: DropdownItem<TItem>;
  onChange?: (state: DropdownItem<TItem>) => void;
  items: DropdownItem<TItem>[];
  disabled?: boolean;
  isLoading?: boolean;
  value?: DropdownItem<TItem>;
  placeholder?: string;
  size?: 'large' | 'medium';
  inlineMessage?: InlineMessageType;
  spaceBottomStyle?: string;
  className?: string;
}
export function Dropdown<TItem>({
  defaultState,
  items,
  title,
  onChange,
  disabled = false,
  isLoading = false,
  placeholder = 'انتخاب کنید',
  size = 'large',
  inlineMessage,
  spaceBottomStyle,
  className
}: Props<TItem>) {
  const [internalValue, setInternalValue] = useState<DropdownItem<TItem> | null>(defaultState || null);
  const handleStateChange = (newState: DropdownItem<TItem>) => {
    if (disabled) return;
    setInternalValue(newState);
    onChange && onChange(newState);
  };
  const showLoading = isLoading && !disabled;
  useEffect(() => {
    setInternalValue(defaultState || null);
  }, [defaultState]);
  return <div className={cn('flex flex-col gap-1 text-body-14 lg:text-body-16', className)} data-sentry-component="Dropdown" data-sentry-source-file="dropdown.tsx">
            {title && <span>{title}</span>}

            {showLoading && <div className='h-[42px] w-full animate-pulse rounded bg-gray-200' />}

            {!showLoading && <Listbox disabled={disabled} value={internalValue} onChange={handleStateChange}>
                    {({
        open
      }) => <>
                            <ListboxButton className={cn('flex w-full items-center justify-between gap-2 rounded border px-2 outline-none', size === 'large' ? 'py-[9px]' : 'py-[7px]',
        // eslint-disable-next-line no-nested-ternary
        inlineMessage?.type === 'error' ? 'border-alert-error-dark focus-within:border-alert-error-dark' : open ? 'border-border-pink' : 'border-border-gray-medium', {
          'bg-surface-solid-50': disabled,
          'bg-surface-solid-0 focus-within:border-border-pink': inlineMessage?.type !== 'error' && !disabled
        })}>
                                <span className={cn('block truncate text-sm lg:text-base',
          // eslint-disable-next-line no-nested-ternary
          disabled ? 'text-text-lightGray' : internalValue?.value ? 'text-text-black' : 'text-text-mediumGray')}>
                                    {internalValue?.label || placeholder}
                                </span>

                                <IconArrowSquareDown className={cn('h-6 w-6 transform transition-all duration-200', disabled ? 'text-text-lightGray' : 'text-text-mediumGray')} />
                            </ListboxButton>

                            <ListboxOptions anchor={{
          gap: '4px'
        }} transition className={cn('w-[var(--button-width)] cursor-pointer list-none overflow-auto rounded border border-border-pink bg-white py-0.5 pl-1 leading-9 shadow-sm focus:outline-none', items.length >= 3 && '!max-h-[130px]')}>
                                {items.map(item => <ListboxOption key={item.value} value={item} as={'div'}>
                                        {({
              focus,
              selected: itemSelected
            }) => <li className={cn(focus && 'duration-400 bg-gray-100 transition-all ease-in-out')}>
                                                <span className={cn(itemSelected && 'font-medium', 'px-4 py-1 text-text-black')}>
                                                    {item.label}
                                                </span>
                                            </li>}
                                    </ListboxOption>)}
                            </ListboxOptions>
                        </>}
                </Listbox>}
            {inlineMessage?.message ? <InlineMessage {...inlineMessage} /> : <div className={cn('h-5 w-full', spaceBottomStyle)} />}
        </div>;
}